
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import Time from './Time';
import HoverLink from './HoverLink';
import AsciiBox from './AsciiBox';
import './index.css';
import { isChrome, isMobile } from 'react-device-detect';

const About = () => {

    const [time, setTime] = useState(0);
    const containerRef = useRef(null);
    const targetMousePos = useRef({ x: 0.5, y: 0.5 });
    const currentMousePos = useRef({ x: 0.5, y: 0.5 });
    const requestRef = useRef();

    const handleMouseMove = useCallback((e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        targetMousePos.current = {
            x: Math.max(0, Math.min(1, x)),
            y: Math.max(0, Math.min(1, y))
        };
    }, []);

    const animate = useCallback((time) => {
        setTime(time * 0.001);

        // Smooth mouse movement
        const ease = 0.05;
        currentMousePos.current.x += (targetMousePos.current.x - currentMousePos.current.x) * ease;
        currentMousePos.current.y += (targetMousePos.current.y - currentMousePos.current.y) * ease;

        requestRef.current = requestAnimationFrame(animate);
    }, []);

    useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current);
    }, [animate]);

    const topoArt = useMemo(() => {
        const width = 100;
        const height = 70;
        // Refined character set for smoother gradients
        const chars = [' ', '.', ',', '-', '~', '+', '=', 'o', 'O', '#', '&', '@'];

        const getHeight = (nx, ny, t) => {
            // Drifting peaks
            const peaks = [
                { x: 0.3 + Math.sin(t * 0.2) * 0.1, y: 0.4 + Math.cos(t * 0.15) * 0.1, strength: 0.8, radius: 0.3 },
                { x: 0.7 + Math.cos(t * 0.17) * 0.1, y: 0.3 + Math.sin(t * 0.22) * 0.1, strength: 0.6, radius: 0.25 },
                { x: 0.5 + Math.sin(t * 0.12) * 0.15, y: 0.7 + Math.cos(t * 0.1) * 0.1, strength: 0.7, radius: 0.35 },
                { x: 0.2 + Math.cos(t * 0.1) * 0.1, y: 0.8 + Math.sin(t * 0.15) * 0.1, strength: 0.4, radius: 0.2 },
                { x: 0.85 + Math.sin(t * 0.25) * 0.05, y: 0.6 + Math.cos(t * 0.2) * 0.05, strength: 0.5, radius: 0.22 },
            ];

            let h = 0;

            // Add base peaks
            for (const peak of peaks) {
                const dx = nx - peak.x;
                const dy = ny - peak.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                h += peak.strength * Math.exp(-dist * dist / (peak.radius * peak.radius));
            }

            // Mouse interaction (using smoothed position)
            const mouseDx = nx - currentMousePos.current.x;
            const mouseDy = ny - currentMousePos.current.y;
            const mouseDist = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

            // Ripple effect
            const ripple = Math.sin(mouseDist * 20 - t * 2) * 0.1 * Math.exp(-mouseDist * 4);
            h += ripple;

            // Cursor influence
            const cursorInfluence = 0.3 * Math.exp(-mouseDist * mouseDist / 0.05);
            h += cursorInfluence;

            // Dynamic noise
            const noise = Math.sin(nx * 30 + t) * Math.cos(ny * 20 - t) * 0.05;
            h += noise;

            return h;
        };

        let result = '';

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const nx = x / width;
                const ny = y / height;

                const h = getHeight(nx, ny, time);

                // Calculate gradient for contour line detection
                const eps = 0.01;
                const hLeft = getHeight(nx - eps, ny, time);
                const hRight = getHeight(nx + eps, ny, time);
                const hUp = getHeight(nx, ny - eps, time);
                const hDown = getHeight(nx, ny + eps, time);

                const gradX = (hRight - hLeft) / (2 * eps);
                const gradY = (hDown - hUp) / (2 * eps);
                const gradMag = Math.sqrt(gradX * gradX + gradY * gradY);

                // Quantize height into contour levels
                const contourLevels = 10;
                const level = Math.floor(h * contourLevels);
                const fractional = (h * contourLevels) % 1;

                // Detect contour lines
                const isContour = fractional < 0.15;

                let charIndex;
                if (isContour && gradMag > 0.3) {
                    // On a contour line - use direction-aware characters
                    const angle = Math.atan2(gradY, gradX);
                    if (Math.abs(angle) < Math.PI / 6 || Math.abs(angle) > 5 * Math.PI / 6) {
                        charIndex = chars.indexOf('-');
                    } else if (Math.abs(angle - Math.PI / 2) < Math.PI / 6 || Math.abs(angle + Math.PI / 2) < Math.PI / 6) {
                        charIndex = chars.indexOf('|');
                    } else if ((angle > Math.PI / 6 && angle < Math.PI / 2) || (angle > -5 * Math.PI / 6 && angle < -Math.PI / 2)) {
                        charIndex = chars.indexOf('/');
                    } else {
                        charIndex = chars.indexOf('\\');
                    }
                    // Fallback if specific char not found (though these are standard)
                    if (charIndex === -1) charIndex = chars.indexOf('+');
                } else {
                    // Fill based on height
                    const scaledHeight = Math.max(0, Math.min(1, h * 0.6));
                    charIndex = Math.floor(scaledHeight * (chars.length - 1));
                }

                result += chars[charIndex] || chars[0];
            }
            result += '\n';
        }

        return result;
    }, [time]);

    return (
        <div style={{ display: "flex", gap: "80px", alignItems: "flex-start", justifyContent: "center", marginTop: "15%", flexDirection: isMobile ? "column" : "row" }}>
            <div style={{ flex: "0 0 auto", width: isMobile ? "100%" : "400px", margin: isMobile ? "0 auto" : "0" }}>
                {isChrome ? (
                    <AsciiBox>Sid Bharthulwar</AsciiBox>
                ) : (
                    <div className="nameBoxDiv">
                    </div>
                )}

                <Time></Time>

                <p>I'm currently a quantitative researcher at <HoverLink text="Jump Trading" href="http://jumptrading.com"></HoverLink> working on AI.</p>

                <p>I studied math and computer science at Harvard, where I worked on reinforcement learning and diffusion models. My <HoverLink routing="/research" text="research" /> was presented at NeurIPS, ICML, ICLR, and RLC.</p>

                <p>I've also interned at <HoverLink text="Two Sigma" href="http://twosigma.com"></HoverLink> and <HoverLink text="Bridgewater Associates" href="http://bridgewater.com"></HoverLink>.</p>

                <p>
                    Feel free to <HoverLink routing="/contact" text="reach out" /> - my DMs are open. </p>

                <img src="https://hitwebcounter.com/counter/counter.php?page=7811144&style=0007&nbdigits=5&type=ip&initCount=0" id="counter" border="0" alt="visitor counter" />
            </div>
            {!isMobile && (
                <div
                    ref={containerRef}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => { targetMousePos.current = { x: 0.5, y: 0.5 } }}
                    style={{
                        flex: "0 0 auto",
                        fontFamily: "monospace",
                        fontSize: "6px",
                        lineHeight: "0.6",
                        letterSpacing: "0px",
                        color: "inherit",
                        opacity: "0.6",
                        whiteSpace: "pre",
                        overflow: "visible",
                        userSelect: "none",
                        paddingTop: "20px",
                        cursor: "crosshair",
                        transition: "opacity 0.2s ease",
                    }}
                >
                    {topoArt}
                </div>
            )}
        </div>
    )
}





export default About
